import { Component } from "react";
import PropTypes from "prop-types";
import { Subject, merge, filter, map } from "rxjs";

import { useApolloClient } from "util/graphql";
import { fromSocketEvent, safeSubscribeToChannel } from "socket/util";

function mapToCacheUpdates({ channel, query, meetingId, client, updateEventConfigs }) {
  const eventEmissions$ = updateEventConfigs.map(([eventName, mapFn]) => {
    return fromSocketEvent(channel, eventName).pipe(
      map((socketMessage) => {
        const data = client.readQuery({
          query,
          variables: { meetingId },
        });
        const { meeting, ...otherData } = data;
        return [mapFn(meeting, socketMessage), otherData];
      }),
    );
  });
  return merge(...eventEmissions$).pipe(
    // Sometimes a socket timing makes a cache write unneeded (perhaps a query or mutation came back faster with
    // the data contained in this event). To avoid potentially expensive cache updates and to make socket events
    // idempotent, we allow update functions to "abort" writes by returning falsey values for new meeting.
    filter(([newMeeting]) => newMeeting),
  );
}

class ObserverSocketMeetingUpdates extends Component {
  componentDidMount() {
    const { channel, meetingId, client, refetch, query, updateEventConfigs, refetchEventConfigs } =
      this.props;
    const unmounted$ = (this.unmounted$ = new Subject());

    safeSubscribeToChannel(
      unmounted$,
      mapToCacheUpdates({
        channel,
        meetingId,
        client,
        query,
        updateEventConfigs,
      }),
      ([meeting, others]) => {
        client.writeQuery({
          query,
          variables: { meetingId },
          data: { meeting, ...others },
        });
      },
    );

    safeSubscribeToChannel(
      unmounted$,
      merge(...refetchEventConfigs.map((eventName) => fromSocketEvent(channel, eventName))),
      () => refetch(),
    );
  }

  componentWillUnmount() {
    this.unmounted$.next();
    this.unmounted$.complete();
    this.unmounted$ = null;
  }

  render() {
    return this.props.children();
  }
}

ObserverSocketMeetingUpdates.propTypes = {
  /** A websocket channel */
  channel: PropTypes.object.isRequired,
  /** The query that will be used to update the cache */
  query: PropTypes.object.isRequired,
  refetch: PropTypes.func.isRequired,
  updateEventConfigs: PropTypes.array.isRequired,
  refetchEventConfigs: PropTypes.array.isRequired,
  meetingId: PropTypes.string.isRequired,
  client: PropTypes.object.isRequired, // apollo
  children: PropTypes.func.isRequired,
};

export default (props) => {
  const client = useApolloClient();
  return <ObserverSocketMeetingUpdates client={client} {...props} />;
};
